import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isHidden, setIsHidden] = useState(false);
  const [isUnderlined, setIsUnderlined] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSubMenu, setActiveSubMenu] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1000);

  // 언어 상태
  const [lang, setLang] = useState(localStorage.getItem('mc_lang') || 'KOR');
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef(null);

  /** 🌍 지구본 아이콘 — 부모 color를 자동 상속 (currentColor) */
  const GlobeIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ display: 'block' }}>
      <path d="M12 21c4.97 0 9-4.03 9-9s-4.03-9-9-9-9 4.03-9 9 4.03 9 9 9Z" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="M3 12h18M12 3c2.5 2.3 3.75 5.3 3.75 9S14.5 18.7 12 21M12 3c-2.5 2.3-3.75 5.3-3.75 9S9.5 18.7 12 21"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );

  /** 🔳 그리드 아이콘 — 부모 color 자동 상속 */
  const GridIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" style={{ display: 'block' }}>
      {[0, 1, 2].flatMap((r) =>
        [0, 1, 2].map((c) => <circle key={`${r}-${c}`} cx={6 + c * 6} cy={6 + r * 6} r="1.4" />)
      )}
    </svg>
  );

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const toggleSubMenu = (menu) => () => setActiveSubMenu(activeSubMenu === menu ? null : menu);

  const handleScroll = () => {
    const newScrollY = window.scrollY;
    setScrollY(newScrollY);
    setIsHidden(newScrollY > 0 && newScrollY <= 350);
    setIsScrolled(newScrollY > 350);
  };

  const handleSubMenuClick =
    (path, category = null) =>
    (e) => {
      e.stopPropagation();
      if (category) navigate(`/board/${category}`);
      else if (path) navigate(path);
      window.scrollTo(0, 0);
      setIsMenuOpen(false);
    };

  const handleProductSubMenuClick = (tab) => (e) => {
    e.stopPropagation();
    navigate(`/board/product/${tab}`);
    window.scrollTo(0, 0);
    setIsMenuOpen(false);
  };

  const changeLang = (next) => {
    setLang(next);
    localStorage.setItem('mc_lang', next);
    setLangOpen(false);
  };

  useEffect(() => {
    document.documentElement.setAttribute('lang', lang === 'KOR' ? 'ko' : 'en');
  }, [lang]);

  useEffect(() => {
    const onClick = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // 스크롤됐을 때만 헤더 흰색으로
  const forceBlack = isScrolled;
  const headerColor = forceBlack ? '#000' : '#fff';

  if (isMobile) {
    // 📱 모바일 헤더
    return (
      <header id="mobile-header" style={{ backgroundColor: forceBlack ? '#fff' : 'transparent' }}>
        <div className="mobile-header-box">
          <div className="mobile-logo" onClick={handleSubMenuClick('/')} style={{ color: headerColor }}>
            MEDICODE
          </div>
          <div className={`hamburger ${isMenuOpen ? 'active' : ''}`} onClick={toggleMenu}>
            <span className="bar"></span>
            <span className="bar"></span>
            <span className="bar"></span>
          </div>

          <div className={`mobile-menu-container ${isMenuOpen ? 'open' : ''}`}>
            <div className="mobile-menu">
              <div className="mobile-menu-title">MEDICODE</div>
              <div className="mobile-menu-close" onClick={toggleMenu}>
                ✕
              </div>

              <ul className="mobile-ul">
                <li onClick={handleSubMenuClick('/medicode')}>기업소개</li>
                <li onClick={handleSubMenuClick(null, 'partnership')}>협력사</li>
                <li onClick={handleProductSubMenuClick('')}>
                  상품
                  {/* <ul className="mobile-submenu">
                    <li onClick={handleProductSubMenuClick('1')}>건강검진</li>
                    <li onClick={handleProductSubMenuClick('2')}>뷰티(줄기세포)</li>
                    <li onClick={handleProductSubMenuClick('3')}>항노화(줄기세포)</li>
                  </ul> */}
                </li>
                <li onClick={handleSubMenuClick(null, 'news')}>뉴스</li>
                <li onClick={handleSubMenuClick(null, 'contact_us')}>CONTACT US</li>
              </ul>

              {/* 언어/사이트맵 */}
              <div className="mobile-lang-row" style={{ color: headerColor }}>
                <div className="lang-pop-wrap" ref={langRef}>
                  <button
                    className="lang-switch-btn"
                    onClick={() => setLangOpen((v) => !v)}
                    aria-label="언어 전환 메뉴 열기"
                    aria-haspopup="true"
                    aria-expanded={langOpen}
                    style={{ color: headerColor }}
                  >
                    <GlobeIcon />
                    <span className="black-s">{lang}</span>
                  </button>

                  {langOpen && (
                    <div className="lang-popover">
                      <button
                        className={`lang-option ${lang === 'KOR' ? 'is-active' : ''}`}
                        onClick={() => changeLang('KOR')}
                      >
                        KOR
                      </button>
                      <button
                        className={`lang-option ${lang === 'ENG' ? 'is-active' : ''}`}
                        onClick={() => changeLang('ENG')}
                      >
                        ENG
                      </button>
                    </div>
                  )}
                </div>

                <button className="sitemap-btn" onClick={handleSubMenuClick('/sitemap')} aria-label="사이트맵">
                  <GridIcon />
                </button>
              </div>

              <div className="mobile-contact">
                <div>T. 010-9915-5706</div>
                <div>E. CS@medicode.co.kr</div>
              </div>
            </div>
            <div className="mobile-menu-backdrop" onClick={toggleMenu}></div>
          </div>
        </div>
      </header>
    );
  }

  // 💻 데스크톱 헤더
  return (
    <header
      id="header"
      className={`${isHidden ? 'hidden' : 'visible'} ${scrollY > 0 && isUnderlined ? 'underline' : ''} ${
        isScrolled ? 'scrolled' : ''
      }`}
      style={{
        backgroundColor: forceBlack ? '#fff' : 'transparent',
        color: headerColor,
      }}
    >
      <div className="header-box">
        <div className="header-left-box">
          <div className="main-logo" onClick={handleSubMenuClick('/')} style={{ color: headerColor }}>
            MEDICODE
          </div>
        </div>

        <div className="header-right-box">
          <ul>
            <li onClick={handleSubMenuClick('/medicode')} style={{ color: headerColor }}>
              기업소개
            </li>
            <li onClick={handleSubMenuClick(null, 'partnership')} style={{ color: headerColor }}>
              협력사
            </li>
            <li onClick={handleSubMenuClick(null, 'product')} style={{ color: headerColor }}>
              상품
              <ul className="submenu">
                <li onClick={handleProductSubMenuClick('1')}>건강검진</li>
                <li onClick={handleProductSubMenuClick('2')}>뷰티(줄기세포)</li>
                <li onClick={handleProductSubMenuClick('3')}>항노화(줄기세포)</li>
              </ul>
            </li>
            <li onClick={handleSubMenuClick(null, 'news')} style={{ color: headerColor }}>
              뉴스
            </li>
            <li onClick={handleSubMenuClick(null, 'contact_us')} style={{ color: headerColor }}>
              CONTACT US
            </li>

            {/* 언어 버튼 + 사이트맵 */}
            <li className="lang-switch" ref={langRef}>
              <button
                className="lang-switch-trigger"
                onClick={() => setLangOpen((v) => !v)}
                aria-label="언어 전환 메뉴 열기"
                aria-haspopup="true"
                aria-expanded={langOpen}
                style={{ color: headerColor }}
              >
                <GlobeIcon />
              </button>

              {langOpen && (
                <div className="lang-popover">
                  <button
                    className={`lang-option ${lang === 'KOR' ? 'is-active' : ''}`}
                    onClick={() => changeLang('KOR')}
                  >
                    KOR
                  </button>
                  <button
                    className={`lang-option ${lang === 'ENG' ? 'is-active' : ''}`}
                    onClick={() => changeLang('ENG')}
                  >
                    ENG
                  </button>
                </div>
              )}
            </li>

            <li className="sitemap-icon">
              <button
                className="sitemap-trigger"
                onClick={handleSubMenuClick('/sitemap')}
                aria-label="사이트맵"
                style={{ color: headerColor }}
              >
                <GridIcon />
              </button>
            </li>
          </ul>
        </div>
      </div>
    </header>
  );
};

export default Header;
